import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  Stack,
  Divider,
  useTheme,
  alpha
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import { usePDF } from "react-to-pdf";
import { useAppDispatch } from "../../../../core/hooks/useStore";
import { RootState } from "../../../../core/store/store";
import { HeadlineJobData } from "../headline-pig.slice";
import HeadlinePigResultsPDF from "./results-pdf";

const HeadlinePigResultsTable = (props: { hideButtons?: boolean }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { apiUrl } = useSelector((state: RootState) => state.home);
  const toolData = useSelector((state: RootState) => state.headlinePig);
  const [searchTerm, setSearchTerm] = useState("");
  const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(null);
  
  const { toPDF, targetRef } = usePDF({
    filename: `headline-report-${new Date().getTime()}.pdf`,
  });

  const exportOpen = Boolean(exportAnchorEl);

  const results = toolData.tool.data
    ? (toolData.tool.data as HeadlineJobData).data.headlines
    : null;

  if (!results) {
    return <></>;
  }

  // Filter results based on search term
  const filteredResults = results.filter(headline => 
    headline.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportPDF = () => {
    toPDF();
    handleExportClose();
  };

  const handleExportCSV = () => {
    handleExportClose();
  };

  const csvData = !results
    ? []
    : [
      ["Headline"],
      ...results?.map((result) => {
        return [result];
      }),
    ];

  return (
    <Box>
      {/* Search Bar and Export Button */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search headlines..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.2),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              }
            }
          }}
        />
        
        <Button
          variant="outlined"
          onClick={handleExportClick}
          startIcon={<DownloadIcon />}
          endIcon={<ArrowDropDownIcon />}
          sx={{
            minWidth: 120,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              bgcolor: alpha(theme.palette.primary.main, 0.05)
            }
          }}
        >
          Export
        </Button>
      </Stack>

      {/* Table */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: theme.shadows[2],
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  fontSize: '0.875rem',
                  width: '60px',
                  bgcolor: alpha(theme.palette.primary.main, 0.05)
                }}
              >
                #
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  fontSize: '0.875rem',
                  bgcolor: alpha(theme.palette.primary.main, 0.05)
                }}
              >
                Headline
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredResults.map((headline, index) => {
              return (
                <TableRow
                  key={index}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: alpha(theme.palette.primary.main, 0.02) 
                    },
                    '&:last-child td, &:last-child th': { border: 0 }
                  }}
                >
                  <TableCell>
                    <Chip
                      label={index + 1}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        minWidth: '32px',
                        userSelect: 'none'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 500,
                        color: theme.palette.text.primary
                      }}
                    >
                      {headline}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {filteredResults.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            bgcolor: alpha(theme.palette.grey[100], 0.5),
            borderRadius: 2,
            border: `2px dashed ${theme.palette.grey[300]}`,
            mt: 3
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No headlines found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search term to find headlines
          </Typography>
        </Box>
      )}

      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        keepMounted
        open={exportOpen}
        onClose={handleExportClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            boxShadow: theme.shadows[4],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }
        }}
      >
        <MenuItem 
          onClick={handleExportPDF}
          sx={{
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.05)
            }
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <DownloadIcon fontSize="small" />
            <span>Export to PDF</span>
          </Stack>
        </MenuItem>
        
        <Divider />
        
        {exportOpen && (
          <Box
            style={{
              position: "absolute",
              top: "-10000px",
            }}
          >
            <div ref={targetRef}>
              <HeadlinePigResultsPDF />
            </div>
          </Box>
        )}
        
        <CSVLink
          data={csvData}
          filename={`headline-report-${new Date().getTime()}.csv`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <MenuItem 
            onClick={handleExportCSV}
            sx={{
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <DownloadIcon fontSize="small" />
              <span>Export to CSV</span>
            </Stack>
          </MenuItem>
        </CSVLink>
      </Menu>
    </Box>
  );
};

export default HeadlinePigResultsTable;